import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { Controller, type FieldError, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import yup from "../../../../utils/yup";

const dividerChoiceSchema = yup.object({
  type: yup
    .string()
    .oneOf(["primary", "secondary"])
    .default(undefined)
    .required("Select a divider"),
});

type DividerChoiceForm = yup.InferType<typeof dividerChoiceSchema>;

export const AddDividerForm: FC<{
  onCancel: () => void;
  onConfirm: (data: DividerChoiceForm) => void;
  open: boolean;
  initialState?: { type?: "primary" | "secondary" };
}> = ({ onCancel, onConfirm, open, initialState }) => {
  const { t } = useTranslation();

  const methods = useForm<DividerChoiceForm>({
    defaultValues: initialState,
    resolver: yupResolver(dividerChoiceSchema),
  });

  const { control, handleSubmit } = methods;

  return (
    <Dialog onClose={onCancel} open={open}>
      <FormProvider {...methods}>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onConfirm)}>
          <DialogTitle>{t("Add Divider")}</DialogTitle>
          <DialogContent sx={{ minWidth: "500px" }}>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DividerSelect onChange={field.onChange} selected={field.value} error={error} />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              data-analytics-id="lexical-divider-dialog-cancel"
              type="button"
              onClick={onCancel}
              color="error"
            >
              {t("Cancel")}
            </Button>
            <Button
              data-analytics-id="lexical-divider-dialog-confirm"
              type="submit"
              color="success"
            >
              {t("Confirm")}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

const DividerSelect: FC<{
  selected?: "primary" | "secondary";
  onChange: (selected: "primary" | "secondary") => void;
  error?: FieldError;
}> = ({ selected, onChange, error }) => {
  return (
    <Stack direction="column" spacing={1}>
      <BrandComponentDivider
        type={"primary"}
        onClick={() => onChange("primary")}
        selected={selected === "primary"}
      />
      <BrandComponentDivider
        type={"secondary"}
        onClick={() => onChange("secondary")}
        selected={selected === "secondary"}
      />
      {error && (
        <Typography color="error" variant="caption">
          {error.message}
        </Typography>
      )}
    </Stack>
  );
};

const BrandComponentDivider: FC<{
  type: "primary" | "secondary";
  selected: boolean;
  onClick?: () => void;
}> = ({ type, selected, onClick }) => {
  return (
    <Button
      data-analytics-id={`lexical-divider-dialog-${type}`}
      onClick={onClick}
      variant="text"
      fullWidth
      sx={
        selected
          ? {
              borderWidth: "3px",
              borderColor: "primary.main",
              borderStyle: "solid",
            }
          : {}
      }
    >
      <Box p={2} sx={{ width: "100%" }} className="divider_button_paper_colour">
        <hr className={type} />
      </Box>
    </Button>
  );
};
